import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, Button, CircularProgress } from '@mui/material';
import { AlertType, appendAlertItem } from '@pruforce/common-adminweb-sdk';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { Link, useHistory, useLocation } from 'react-router-dom';
import AlertDialog from 'src/app/common/components/AlertDialog';
import { alertDialog } from 'src/utils/alertDialog';
import { fetchBdmHoldPolicies, reassignBdmHoldPolicies } from '../../../network/opm-pd-bdm';
import {
  ReassignAgentForHoldPoliciesContext,
  ReassignAgentForHoldPoliciesContextType,
} from './ReassignAgentForHoldPolicies.context';
import BatchFileActions from './components/BatchFileActions/BatchFileActions';
import PolicyTable from './components/PolicyTable/PolicyTable';

const ReassignAgentForHoldPolicies = () => {
  const intl = useIntl();
  const Translation = (id: string, params?: Record<string, any>) => intl.formatMessage({ id }, params);
  const dispatch = useDispatch();
  const history = useHistory();
  const { search } = useLocation();

  const [bdmHoldPolicies, setBdmHoldPolicies] = useState<ReassignAgentForHoldPoliciesContextType['bdmHoldPolicies']>(
    [],
  );
  const [bdmHoldPoliciesLoading, setBdmHoldPoliciesLoading] =
    useState<ReassignAgentForHoldPoliciesContextType['bdmHoldPoliciesLoading']>(false);
  const [formPolicies, setFormPolicies] = useState<ReassignAgentForHoldPoliciesContextType['formPolicies']>([]);
  const [deletedPolicyIds, setDeletedPolicyIds] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formPoliciesRef = useRef(formPolicies);

  const policyIds = useMemo(() => {
    const policyIdsParam = new URLSearchParams(search).get('policyIds');

    return policyIdsParam
      ? policyIdsParam
          .split(',')
          .map((policyId) => policyId.trim())
          .filter(Boolean)
      : [];
  }, [search]);

  const fetchHoldPoliciesData = async () => {
    setBdmHoldPoliciesLoading(true);
    try {
      const data = await fetchBdmHoldPolicies(
        {
          limit: policyIds.length,
          page: 1,
          policyIds,
        },
        dispatch,
      );
      const bdmHoldPolicies = data?.items ?? [];
      setBdmHoldPolicies(bdmHoldPolicies);
      setFormPolicies(
        bdmHoldPolicies.map((policy, index) => ({
          ...policy,
          qualifiedAgents: policy.qualifiedAgents?.filter((agent) => agent.agentCode !== policy.suggestedAgent) ?? [],
          _index: index,
          _selectedOtherQualifiedAgent: null,
          _selectedOtherAgentReason: '',
          _displaySelectedAgent: policy.selectedAgent!,
          _displaySelectedAgentName: policy.selectedAgentName!,
          _isDeleted: false,
        })),
      );
    } catch (error) {
      history.push('/opm-pd/policy-transfer-request/batch-files');
    } finally {
      setBdmHoldPoliciesLoading(false);
    }
  };

  const setPolicyFieldValue: ReassignAgentForHoldPoliciesContextType['setPolicyFieldValue'] = (index, field, value) => {
    const newPolicies = [...formPoliciesRef.current];
    newPolicies[index] = {
      ...newPolicies[index],
      [field]: value,
    };
    formPoliciesRef.current = newPolicies;
    setFormPolicies(newPolicies);
  };

  const addOtherQualifiedAgentToPolicy = (
    qualifiedAgents: {
      agentCode: string;
      fullname: string;
      label: string;
      policyNumbers: string[];
    }[],
  ) => {
    setFormPolicies((prevPolicies) => {
      const updatedPolicies = prevPolicies.map((policy) => {
        const matchedAgent = qualifiedAgents.find((agent) => agent.policyNumbers.includes(policy.policyNumber));
        if (matchedAgent && !policy._selectedOtherQualifiedAgent) {
          return {
            ...policy,
            _selectedOtherQualifiedAgent: matchedAgent,
            _displaySelectedAgent: matchedAgent.agentCode,
            _displaySelectedAgentName: matchedAgent.fullname,
          };
        }
        return policy;
      });
      return updatedPolicies;
    });
  };

  const removeFormPolicy = (index: number | number[]) => {
    setFormPolicies((prevPolicies) => {
      const indices = Array.isArray(index) ? index : [index];
      setDeletedPolicyIds((prev) => [
        ...prev,
        ...prevPolicies.filter((policy) => indices.includes(policy._index!)).map((policy) => policy.id),
      ]);
      return prevPolicies.map((policy) => {
        if (indices.includes(policy._index!)) {
          return { ...policy, _isDeleted: true };
        }
        return policy;
      });
    });
  };

  const _submitBdmHoldPolicies = async () => {
    let policies = formPolicies.filter((policy) => !policy._isDeleted);
    const invalidSelectedOtherAgentReasonPolicies = policies.filter((policy) => {
      return !!policy._selectedOtherQualifiedAgent && !policy._selectedOtherAgentReason;
    });
    if (invalidSelectedOtherAgentReasonPolicies.length > 0) {
      dispatch(
        appendAlertItem([
          {
            severity: AlertType.ERROR,
            title: Translation('opm_pd.invalidInput'),
            content: Translation('opm_pd.provideReasonForSelectingAnotherAgentText', {
              policies: invalidSelectedOtherAgentReasonPolicies.map((policy) => policy.policyNumber).join(', '),
            }),
          },
        ]),
      );
      return;
    }

    const notQualifiedAgentPolicyIds = policies
      .filter((policy) => {
        return !policy._displaySelectedAgent;
      })
      .map((policy) => policy.id);
    policies = policies.filter((policy) => !notQualifiedAgentPolicyIds.includes(policy.id));

    if (policies.length === 0) {
      dispatch(
        appendAlertItem([
          {
            severity: AlertType.ERROR,
            title: Translation('opm_pd.invalidInput'),
            content: Translation('opm_pd.noQualifiedAgent'),
          },
        ]),
      );
      return;
    }

    if (notQualifiedAgentPolicyIds.length > 0) {
      const { isConfirmed: isConfirmedSecond } = await alertDialog.fire({
        title: Translation('opm_pd.alert'),
        description: Translation('opm_pd.alertNumberOfPoliciesNotQualifiedAgentText', {
          count: notQualifiedAgentPolicyIds.length,
        }),
      });
      if (!isConfirmedSecond) return;
    }

    const { isConfirmed } = await alertDialog.fire({
      title: Translation('opm_pd.confirmSubmitBatchFilePoliciesText'),
      description: Translation('opm_pd.confirmSubmitBatchFilePoliciesContentText'),
    });
    if (!isConfirmed) return;
    setIsSubmitting(true);
    try {
      await reassignBdmHoldPolicies(
        {
          policies: policies.map((policy) => ({
            policyId: policy.id,
            selectedAgent: policy._selectedOtherQualifiedAgent?.agentCode,
            selectedAgentName: policy._selectedOtherQualifiedAgent?.fullname,
            reasonSelectedAnotherAgent: !!policy._selectedOtherQualifiedAgent
              ? policy._selectedOtherAgentReason
              : undefined,
          })),
          policiesDeleted: [...deletedPolicyIds, ...notQualifiedAgentPolicyIds],
        },
        dispatch,
      );
      dispatch(
        appendAlertItem([
          {
            severity: AlertType.SUCCESS,
            title: Translation('opm_pd.success'),
            content: Translation('opm_pd.reassignPoliciesSuccessfully'),
          },
        ]),
      );
      history.push('/opm-pd/policy-transfer-request/batch-files');
    } catch (error) {
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    fetchHoldPoliciesData();
  }, [policyIds]);

  useEffect(() => {
    formPoliciesRef.current = formPolicies;
  }, [formPolicies]);

  return (
    <ReassignAgentForHoldPoliciesContext.Provider
      value={{
        bdmHoldPolicies,
        bdmHoldPoliciesLoading,
        formPolicies,
        isSubmitting,
        submitBdmHoldPolicies: _submitBdmHoldPolicies,
        setPolicyFieldValue,
        addOtherQualifiedAgentToPolicy,
        removeFormPolicy,
      }}
    >
      <AlertDialog />
      <Box display="flex" flexDirection="column" gap={2}>
        <Box display="flex" justifyContent="flex-start" gap={2}>
          <Button
            startIcon={<ArrowBackIcon />}
            variant="outlined"
            color="error"
            component={Link}
            to="/opm-pd/policy-transfer-request/batch-files"
          >
            Back
          </Button>
        </Box>
        {bdmHoldPoliciesLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <PolicyTable />
            <BatchFileActions />
          </>
        )}
      </Box>
    </ReassignAgentForHoldPoliciesContext.Provider>
  );
};

export default ReassignAgentForHoldPolicies;
